import React, { useState, useEffect } from "react";
import "./App.css";
import "./input.css";
import { Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import ParentLogin from "./components/ParentLogin";
import PediatricianLogin from "./components/PediatricianLogin";
import SpecialistLogin from "./components/SpecialistLogin";

import ParentSignup from "./components/ParentSignup";
import PediatricianSignup from "./components/PediatricianSignup";
import SpecialistSignup from "./components/SpecialistSignup";

import ParentProfile from "./components/ParentProfile";
import PediatricianProfile from "./components/PediatricianProfile";
import SpecialistProfile from "./components/SpecialistProfile";

import PatientForm from "./components/PatientForm";

import ChatWindow from "./components/ChatWindow";
import VaccinationTracker from "./components/VaccinationTracker";
import MilestoneTracker from "./components/MilestoneTracker";
import UpdatePatient from "./components/UpdatePatient";
import NewAddVisitRecordForm from "./components/NewAddVisitRecordForm";
import PatinetProfilePage from "./components/PatinetProfilePage";
import EducationalMaterial from "./components/EducationalMaterial";
import { toast, ToastContainer } from "react-toastify";
import ParentMilstoneUpdate from "./components/ParentMilestoneUpdate";
import Loader from "./components/Loader";
import capitailzer from "./util/capitalize";
import ParentAddChildForm from "./components/ParentAddChild";
import ForgotPassword from "./components/ForgotPassword";
import MediaUploadByParent from "./components/MediaUploadByParent";
import PediatricianDashboard from "./components/PediatricianDashboard";
import { useParentContext } from "./context/ParentContext";
import RequestForDoctor from "./components/RequestForDoctor";
import RootLayout from "./RootLayout";
import { languages } from "./locals/languageCode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserAlt } from "@fortawesome/free-solid-svg-icons";
import MyUpcomingTasks from "./components/MyUpcomingTasks";
import PatientSharing from "./components/PatientSharing";
import { useDoctorContext } from "./context/DoctorContext";
import { initWebPush } from "./notification/notificationService";
import { initFirebase } from "./notification/firebase";
import Footer from "./components/Footer";
import LegalSupport from "./components/LegalSupport";
import ChildConsentManagement from "./components/ChildConsentManagement";

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // To Track the role of the user (parent, pediatrician, or specialist)
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const logo = process.env.PUBLIC_URL + "/images/logo.png";
  const token = localStorage.getItem("s2stoken");
  const { setParentState, language, handleLanguageChange, resetParentContext } = useParentContext();
  const { clearDoctorsStates } = useDoctorContext();

  useEffect(() => {
    if (currentUser && userRole === "parent") {
      navigate("/parent-profile");
    } else if (currentUser && userRole === "pediatrician") {
      navigate("/pediatrician-dashboard");
    }
  }, [currentUser, userRole]);

  useEffect(() => {
    async function initFirebaseAndSendToken() {
      await initFirebase();
      if (userRole === "parent") {
        await initWebPush(currentUser?.parent_id);
      } else if (userRole === "pediatrician") {
        await initWebPush(null, currentUser?.pediatrician_id);
      }
    }
    if (currentUser) {
      initFirebaseAndSendToken();
    }
  }, [currentUser, userRole]);

  const handleLogin = async (credentials) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(credentials), // credentials includes identifier, password, and user_type
          credentials: "include",
        },
      );
      const data = await response.json();
      console.log("jwt rres", data);
      localStorage.setItem("s2stoken", data.access_token);
      localStorage.setItem("s2srefresh", data.refresh_token);
      if (response.ok) {
        const profileResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              Authorization: `Bearer ${data.access_token}`,
            },
          },
        );
        const profileData = await profileResponse.json();
        if (profileResponse.ok) {
          setLoading(false);
          setCurrentUser(profileData.user);
          setUserRole(profileData.user_type);
          if (profileData.user_type === "parent") {
            setParentState(data.user);
            navigate("/parent-profile");
          } else if (profileData.user_type === "pediatrician") {
            navigate("/pediatrician-dashboard");
          } else if (profileData.user_type === "specialist") {
            navigate("/patient-profile");
          }
        } else {
          setLoading(false);
          toast.error(profileData.message);
        }
      } else {
        setLoading(false);
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Login error:", error);
      alert("Login failed. Please try again.");
      setLoading(false);
    }
  };

  const handleSignup = async (credentials) => {
    try {
      setLoading(true);
      setCurrentUser(null);
      setUserRole(null);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/signup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(credentials), // credentials includes formData and user_type
          credentials: "include",
        },
      );
      const data = await response.json();
      console.log("Signup response:", data);
      if (response.ok) {
        setLoading(false);
        if (data.user_type === "parent") {
          toast.success(`${capitailzer(data?.user_type)} Sign-up Successful`);
        } else if (data.user_type === "pediatrician") {
          toast.success(
            `${capitailzer(
              data?.user?.pediatrician_specialization.replace("_", " "),
            )} Sign-up Successful`,
          );
        }
        // Navigating to the correct profile based on userRole
        if (data.user_type === "parent") {
          navigate("/parent-login");
        } else if (data.user_type === "pediatrician") {
          navigate("/pediatrician-login");
        } else if (data.user_type === "specialist") {
          navigate("/specialist-login");
        }
      } else {
        setLoading(false);
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Signup error:", error);
      setLoading(false);
      toast.error("Signup failed. Please try again.");
    }
  };

  const handleLogout = async () => {
    setCurrentUser(null);
    setUserRole(null);
    clearDoctorsStates();
    resetParentContext();
    localStorage.removeItem("s2stoken");
    localStorage.removeItem("s2srefresh");
    navigate("/");
  };

  const handleUpdateProfile = async (formData) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/update_profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify(formData),
        },
      );
      console.log(formData);

      if (response.ok) {
        setLoading(false);
        const profileResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const updatedData = await profileResponse.json();
        if (profileResponse.ok) {
          setCurrentUser(updatedData.user);
        }
      } else {
        const data = await response.json();
        setLoading(false);
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Profile update failed:", error);
      setLoading(false);
    }
  };
  console.log(currentUser, "currentuser");
  // Menu options based on user role
  const renderMenu = () => {
    if (!currentUser) return null;

    let menuItems = [];

    if (userRole === "parent") {
      menuItems = [
        <Link to="/parent-profile">Child Profile</Link>,
        <Link to="/chat">Ask Anything</Link>,
        // <a
        //   href="https://smile2stepsplaytime.onrender.com"
        //   target="_blank"
        //   rel="noopener noreferrer"
        // >
        //   Parent Playtime
        // </a>,
        <Link to={`parent-information/${currentUser?.parent_id}`}>
          Educational Videos
        </Link>,
        <Link to={"legal-support"}>Legal & Support</Link>,
      ];
    } else if (userRole === "pediatrician" || userRole === "health_worker") {
      menuItems = [
        <Link to="/pediatrician-dashboard">My Dashboard</Link>,
        <Link to="/chat">Hey Lisha</Link>,
        // <Link to="/patient-profile">New Patient Registration</Link>,
        // <Link to={`/pediatrician-information/${currentUser?.pediatrician_id}`}>
        //   Educational Material
        // </Link>,
        // <Link to="/upcomming-tasks">My Upcoming Tasks</Link>,
        <Link to="/patient-access-management">Patient Referrals</Link>,
        <Link to="/pediatrician-profile">My Profile</Link>,
        <Link to={"legal-support"}>Legal & Support</Link>,
      ];
    } else if (userRole === "specialist") {
      menuItems = [
        <Link to="/patient-profile">Patient Record</Link>,
        <Link to="/specialist-profile">Profile</Link>,
        <Link to={"legal-support"}>Legal & Support</Link>,
      ];
    }

    return (
      <div className="menu-right">
        <div className="nav-dropdown">
          <button className="dropbtn">
            <FontAwesomeIcon icon={faUserAlt} />
          </button>
          <div className="dropdown-content">
            {menuItems}
            <button onClick={handleLogout}>Log Out</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="app-shell">
      <Loader isLoading={loading} />
      <nav className={!currentUser || !token ? "hidden" : ""}>
        <img src={logo} alt="logo" />
        <select value={language} onChange={handleLanguageChange}>
          {languages.map((lang) => (
            <option
              translate="no"
              id={`lang-opt-${lang.code}`}
              key={lang.code}
              value={lang.code}
            >
              {lang.label}
            </option>
          ))}
        </select>
        {renderMenu()}
      </nav>
      <Routes>
        <Route
          path="/parent-login"
          element={
            <ParentLogin
              handleLogin={(credentials) => handleLogin(credentials)}
            />
          }
        />
        <Route
          path="/pediatrician-login"
          element={
            <PediatricianLogin
              handleLogin={(credentials) => handleLogin(credentials)}
            />
          }
        />
        <Route
          path="/specialist-login"
          element={
            <SpecialistLogin
              handleLogin={(credentials) => handleLogin(credentials)}
            />
          }
        />
        <Route
          path="/parent-signup"
          element={
            <ParentSignup
              handleSignup={(credentials) => handleSignup(credentials)}
            />
          }
        />
        <Route
          path="/pediatrician-signup"
          element={
            <PediatricianSignup
              handleSignup={(credentials) => handleSignup(credentials)}
            />
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/legal-support" element={<LegalSupport userRole={userRole}/>} />
        <Route
          path="/specialist-signup"
          element={
            <SpecialistSignup
              handleSignup={(credentials) => handleSignup(credentials)}
            />
          }
        />
        <Route
          path="/child-consent-management"
          element={<ChildConsentManagement />}
        />
        <Route
          element={
            <RootLayout
              setLoading={setLoading}
              setCurrentUser={setCurrentUser}
              setUserRole={setUserRole}
            />
          }
        >
          <Route
            path="/"
            element={
              <ParentLogin
                handleLogin={(credentials) => handleLogin(credentials)}
                activeTab={true}
              />
            }
          />
          <Route
            path="/parent-add-child/:docId"
            element={<ParentAddChildForm />}
          />
          <Route
            path="/doctor-add-child/:childId/:parentId"
            element={<ParentAddChildForm />}
          />
        </Route>

        {currentUser && userRole === "parent" && (
          <>
            <Route
              path="/parent-profile"
              element={
                <ParentProfile
                  currentUser={currentUser}
                  setCurrentUser={setCurrentUser}
                  handleUpdateProfile={handleUpdateProfile}
                />
              }
            />
            <Route
              path="/chat"
              element={
                <ChatWindow currentUser={currentUser} userRole={userRole} />
              }
            />

            <Route
              path="/parent-information/:parentId"
              element={<EducationalMaterial />}
            />
            <Route
              path="/update-milestonebyparent/:docId/:id/:childId"
              element={<ParentMilstoneUpdate />}
            />
            <Route
              path="/request-for-doctor/:childId"
              element={<RequestForDoctor />}
            />
            <Route
              path="/upload-media-by-parent/:docId/:id/:childId"
              element={<MediaUploadByParent />}
            />

            <Route
              path="/vaccination-tracker"
              element={<VaccinationTracker />}
            />
            <Route path="/milestone-tracker" element={<MilestoneTracker />} />
            <Route
              path="/update-patient/:id/:docId"
              element={<UpdatePatient />}
            />
          </>
        )}

        {currentUser && userRole === "pediatrician" && (
          <>
            <Route path="/update-patient" element={<UpdatePatient />} />
            <Route
              path="/update-patient/:id/:docId"
              element={<UpdatePatient currentUser={currentUser} />}
            />
            <Route
              path="/pediatrician-information/:docId"
              element={<EducationalMaterial />}
            />
            <Route
              path="/chat"
              element={
                <ChatWindow currentUser={currentUser} userRole={userRole} />
              }
            />

            <Route
              path="/update-patient/:id/:docId"
              element={<UpdatePatient />}
            />
            <Route
              path="/upcomming-tasks"
              element={<MyUpcomingTasks docId={currentUser?.pediatrician_id} />}
            />
            <Route
              path="/patient-access-management"
              element={<PatientSharing docId={currentUser?.pediatrician_id} />}
            />
            <Route
              path="/new-add-visit-record/:id/:docId/:ageGroup/:currentAge/:ageInDays/:actualMonths/:actualDays"
              element={
                <NewAddVisitRecordForm
                  providerType={currentUser?.pediatrician_specialization}
                />
              }
            />
            <Route
              path="/new-patient-profile/:id/:docId"
              element={<PatinetProfilePage />}
            />
            <Route
              path="/pediatrician-dashboard"
              element={<PediatricianDashboard currentUser={currentUser} />}
            />
            <Route
              path="/patient-profile"
              element={
                <PatientForm pediatricianId={currentUser.pediatrician_id} />
              }
            />
            <Route
              path="/pediatrician-profile"
              element={
                <PediatricianProfile
                  currentUser={currentUser}
                  setCurrentUser={setCurrentUser}
                  handleUpdateProfile={handleUpdateProfile}
                />
              }
            />
          </>
        )}

        {currentUser && userRole === "specialist" && (
          <>
            <Route path="/patient-profile" element={<PatientForm />} />
            <Route
              path="/specialist-profile"
              element={
                <SpecialistProfile
                  currentUser={currentUser}
                  setCurrentUser={setCurrentUser}
                  handleUpdateProfile={handleUpdateProfile}
                />
              }
            />
          </>
        )}

        {!currentUser && <Route path="*" element={<Navigate to="/" />} />}
      </Routes>
      {/* <Footer /> */}
      <ToastContainer
        toastClassName="dynamic-toast"
        position="top-center"
        autoClose={4000}
      />
    </div>
  );
};

export default App;